import React, { useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;

const analyticsSelectionKey = (mrsId) => `analytics_selected_${mrsId}`;
const MAX_FILES = 5;   // how many files a user may select & upload in one go

const FileUpload = () => {
  const { mrsId } = useParams();
  const { user } = useAuth();

  const [dragActive, setDragActive]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);   // up to MAX_FILES at once

  // Datasets for AI analysis (loaded from analytics endpoint)
  const [analyticsDatasets, setAnalyticsDatasets]   = useState([]);
  const [analyticsSelected, setAnalyticsSelected]   = useState(new Set());
  const [analyticsLoading, setAnalyticsLoading]     = useState(true);

  // Load existing analytics datasets on mount
  useEffect(() => {
    if (!mrsId || !user?.id) return;
    setAnalyticsLoading(true);
    axios
      .get(`${ANALYTICS_API}/mrs/${mrsId}/datasets?user_id=${user.id}`)
      .then((res) => {
        const datasets = res.data.datasets || [];
        setAnalyticsDatasets(datasets);
        // Default: all datasets selected. Only exclude ones the user explicitly deselected.
        const allIds = new Set(datasets.map(d => d.dataset_id));
        const savedDeselected = localStorage.getItem(analyticsSelectionKey(mrsId));
        if (savedDeselected) {
          try {
            const deselectedIds = JSON.parse(savedDeselected);
            const selected = new Set([...allIds].filter(id => !deselectedIds.includes(id)));
            setAnalyticsSelected(selected);
          } catch {
            setAnalyticsSelected(allIds);
          }
        } else {
          setAnalyticsSelected(allIds);
        }
      })
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
  }, [mrsId, user?.id]);

  // Persist deselected IDs to localStorage (selected = all minus deselected).
  // Guard: don't write until datasets have loaded — otherwise this runs once on mount with an EMPTY
  // list and overwrites a previously-saved selection with [] before the loader can read it (which
  // made deselections vanish on refresh).
  useEffect(() => {
    if (!mrsId || analyticsLoading || analyticsDatasets.length === 0) return;
    const deselected = analyticsDatasets
      .map(d => d.dataset_id)
      .filter(id => !analyticsSelected.has(id));
    localStorage.setItem(analyticsSelectionKey(mrsId), JSON.stringify(deselected));
  }, [analyticsSelected, analyticsDatasets, analyticsLoading, mrsId]);

  const refreshAnalyticsDatasets = async (newDatasetIds) => {
    try {
      const res = await axios.get(`${ANALYTICS_API}/mrs/${mrsId}/datasets?user_id=${user.id}`);
      const datasets = res.data.datasets || [];
      setAnalyticsDatasets(datasets);
      const ids = Array.isArray(newDatasetIds) ? newDatasetIds : (newDatasetIds ? [newDatasetIds] : []);
      if (ids.length) {
        setAnalyticsSelected(prev => new Set([...prev, ...ids]));
      }
    } catch {}
  };

  const toggleAnalyticsDataset = (datasetId) => {
    setAnalyticsSelected(prev => {
      const next = new Set(prev);
      if (next.has(datasetId)) next.delete(datasetId); else next.add(datasetId);
      return next;
    });
  };

  // â”€â”€ Drag handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFilesSelect(e.dataTransfer.files);
  }, []); // eslint-disable-line

  const isPdf = (file) => file?.name?.toLowerCase().endsWith('.pdf');

  // Validate each incoming file, merge into the selection (dedup by name+size), cap at MAX_FILES.
  const handleFilesSelect = (fileList) => {
    const incoming = Array.from(fileList || []);
    const valid = [];
    for (const file of incoming) {
      if (!file.name.toLowerCase().match(/\.(xlsx|xls|csv|pdf)$/)) {
        alert(`"${file.name}" is not a supported type (Excel, CSV, or PDF) — skipped.`);
        continue;
      }
      if (file.size > 50 * 1024 * 1024) {
        alert(`"${file.name}" is larger than 50MB — skipped.`);
        continue;
      }
      valid.push(file);
    }
    setSelectedFiles(prev => {
      const merged = [...prev];
      for (const f of valid) {
        if (!merged.some(m => m.name === f.name && m.size === f.size)) merged.push(f);
      }
      if (merged.length > MAX_FILES) {
        alert(`You can upload up to ${MAX_FILES} files at once — keeping the first ${MAX_FILES}.`);
      }
      return merged.slice(0, MAX_FILES);
    });
    setUploadResult(null);
  };

  const removeFileAt = (idx) => setSelectedFiles(prev => prev.filter((_, i) => i !== idx));

  // â”€â”€ Upload ONE file: patient endpoint (Excel/CSV) + analytics endpoint â”€â”€â”€â”€
  // Returns a per-file result; never throws, so one bad file can't stop the batch.
  const uploadOne = async (file) => {
    const isPdfFile = file.name.toLowerCase().endsWith('.pdf');

    // 1. Upload as patient records (Excel/CSV only — patient endpoint rejects PDFs)
    let patientResult = null;
    if (!isPdfFile) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(
          `${API}/mrs/${mrsId}/upload?user_id=${user.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        patientResult = { success: true, message: res.data.message };
      } catch (err) {
        patientResult = { success: false, message: err.response?.data?.detail || 'Patient data upload failed.' };
      }
    } else {
      patientResult = { success: true, message: 'PDF registered as an AI analysis dataset.' };
    }

    // 2. Also register as analytics dataset (powers the AI engine). Surface failures.
    let newDatasetId = null;
    let analyticsError = null;
    try {
      const analyticsForm = new FormData();
      analyticsForm.append('file', file);
      const aRes = await axios.post(
        `${ANALYTICS_API}/datasets/upload?mrs_id=${mrsId}&user_id=${user.id}`,
        analyticsForm,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      newDatasetId = aRes.data.dataset_id;
    } catch (err) {
      const detail = err.response?.data?.detail;
      analyticsError =
        (detail && (detail.message || detail.error_code)) ||
        err.response?.data?.message ||
        err.message ||
        'Analytics registration failed.';
    }

    return {
      fileName: file.name,
      newDatasetId,
      success: !analyticsError && patientResult.success,
      message: analyticsError
        ? `${patientResult.message ? patientResult.message + ' ' : ''}However, this file could NOT be prepared for AI analysis: ${analyticsError}`
        : patientResult.message,
    };
  };

  // â”€â”€ Upload all selected files (up to MAX_FILES), one at a time, then show a combined result â”€â”€
  const uploadFiles = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    setUploadResult(null);

    const results = [];
    for (const file of selectedFiles) {
      results.push(await uploadOne(file));   // sequential — one request at a time
    }

    setSelectedFiles([]);
    setUploading(false);
    setUploadResult({ multiple: true, success: results.every(r => r.success), results });

    const newIds = results.map(r => r.newDatasetId).filter(Boolean);
    if (newIds.length || results.some(r => r.success)) {
      await refreshAnalyticsDatasets(newIds);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/mrs" className="text-gray-400 hover:text-gray-600">
          <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Upload Medical Data</h1>
          <p className="text-gray-600 mt-2">Upload Excel or CSV files to add patient data and enable AI analysis</p>
        </div>
      </div>

      {/* Guidelines */}
      <div className="medical-card bg-sky-50 border-sky-200">
        <div className="medical-card-content">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-sky-100 rounded-lg">
              <svg className="h-6 w-6 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-sky-900 mb-2">File Upload Guidelines</h3>
              <div className="space-y-1 text-sm text-sky-800">
                {[
                  'Supported formats: Excel (.xlsx, .xls), CSV, and PDF files',
                  'Maximum file size: 50MB',
                  'Include columns for demographics, medical history, therapy details, and outcomes',
                  'Uploaded files are added to patient records and made available for AI analysis',
                  'PDF files are registered as AI analysis datasets only (patient records require Excel/CSV)',
                ].map((txt, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-sky-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{txt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* â”€â”€ Single Upload Area â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="medical-card">
        <div className="medical-card-content">
          <div
            className={`upload-dropzone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <svg className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>

              {selectedFiles.length > 0 ? (
                <div className="space-y-4">
                  <div className="space-y-2 max-w-md mx-auto">
                    {selectedFiles.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center space-x-3 min-w-0">
                          <svg className="h-7 w-7 text-green-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <div className="text-left min-w-0">
                            <div className="font-medium text-green-900 text-sm truncate">{file.name}</div>
                            <div className="text-xs text-green-700">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                          </div>
                        </div>
                        <button onClick={() => removeFileAt(idx)} disabled={uploading} className="text-gray-400 hover:text-red-600 shrink-0 ml-3" title="Remove">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">{selectedFiles.length} of {MAX_FILES} files selected</p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <button onClick={() => setSelectedFiles([])} disabled={uploading} className="btn-secondary">Remove All</button>
                    {selectedFiles.length < MAX_FILES && (
                      <label htmlFor="file-input" className="btn-secondary cursor-pointer">Add More</label>
                    )}
                    <button onClick={uploadFiles} disabled={uploading} className="btn-primary flex items-center space-x-2">
                      {uploading ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Uploading...</span></>
                      ) : (
                        <><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9.293 2.293a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414-1.414l3-3zM3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg><span>Upload {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''}</span></>
                      )}
                    </button>
                  </div>
                  <input type="file" accept=".xlsx,.xls,.csv,.pdf" multiple onChange={(e) => { handleFilesSelect(e.target.files); e.target.value = ''; }} className="hidden" id="file-input" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Drop your medical data files here</h3>
                    <p className="text-gray-600">or click to browse — up to {MAX_FILES} files at once</p>
                  </div>
                  <input type="file" accept=".xlsx,.xls,.csv,.pdf" multiple onChange={(e) => { handleFilesSelect(e.target.files); e.target.value = ''; }} className="hidden" id="file-input" />
                  <label htmlFor="file-input" className="btn-primary cursor-pointer inline-flex items-center space-x-2">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    <span>Select Files</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div className={`medical-card ${uploadResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="medical-card-content">
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-lg ${uploadResult.success ? 'bg-green-100' : 'bg-red-100'}`}>
                {uploadResult.success ? (
                  <svg className="h-6 w-6 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                ) : (
                  <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold mb-1 ${uploadResult.success ? 'text-green-900' : 'text-red-900'}`}>
                  {uploadResult.multiple
                    ? (uploadResult.success
                        ? `All ${uploadResult.results.length} files uploaded`
                        : `${uploadResult.results.filter(r => r.success).length} of ${uploadResult.results.length} files uploaded`)
                    : (uploadResult.success ? 'Upload Successful!' : 'Upload Failed')}
                </h3>
                {uploadResult.multiple ? (
                  <ul className="space-y-1 mt-1">
                    {uploadResult.results.map((r, i) => (
                      <li key={i} className={`text-sm flex items-start gap-2 ${r.success ? 'text-green-800' : 'text-red-800'}`}>
                        <span className="shrink-0 font-semibold">{r.success ? '✓' : '✕'}</span>
                        <span className="min-w-0"><span className="font-medium">{r.fileName}</span>{r.success ? '' : ` — ${r.message}`}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={`text-sm ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>{uploadResult.message}</p>
                )}
                {(uploadResult.success || (uploadResult.results && uploadResult.results.some(r => r.success))) && (
                  <div className="mt-3 flex space-x-3">
                    <Link to={`/mrs/${mrsId}/patients`} className="btn-primary text-sm">View Patient Records</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* â”€â”€ AI Analysis Datasets â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="medical-card">
        <div className="medical-card-header">
          <div>
            <h3 className="font-semibold text-gray-900">AI Analysis Datasets</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Uploaded files appear here. Check the ones you want included when you click "Analyze your data with AI".
            </p>
          </div>
        </div>
        <div className="medical-card-content">
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-sky-600 mr-3"></div>
              <span className="text-gray-500 text-sm">Loading datasets...</span>
            </div>
          ) : analyticsDatasets.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <svg className="h-12 w-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm font-medium text-gray-500">No datasets yet</p>
              <p className="text-xs text-gray-400 mt-1">Upload a file above to get started.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-sky-700">{analyticsSelected.size}</span> of {analyticsDatasets.filter(d => d.file_type !== 'pdf_table').length} selected for AI analysis
                </p>
                <div className="flex gap-3 text-xs">
                  <button onClick={() => setAnalyticsSelected(new Set(analyticsDatasets.filter(d => d.file_type !== 'pdf_table').map(d => d.dataset_id)))} className="text-sky-600 hover:text-sky-800 underline">Select all</button>
                  <button onClick={() => setAnalyticsSelected(new Set())} className="text-gray-400 hover:text-gray-600 underline">Deselect all</button>
                </div>
              </div>
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                {analyticsDatasets.filter(d => d.file_type !== 'pdf_table').map((ds) => (
                  <label
                    key={ds.dataset_id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${analyticsSelected.has(ds.dataset_id) ? 'bg-sky-50' : 'bg-white hover:bg-gray-50'}`}
                  >
                    <input
                      type="checkbox"
                      checked={analyticsSelected.has(ds.dataset_id)}
                      onChange={() => toggleAnalyticsDataset(ds.dataset_id)}
                      className="h-4 w-4 text-sky-600 border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{ds.file_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {ds.file_type === 'pdf' ? (
                          <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">PDF</span>
                        ) : ds.file_type === 'pdf_table' ? (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">PDF Table</span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">{ds.file_type?.toUpperCase()}</span>
                        )}
                        {ds.file_type !== 'pdf' && (
                          <span className="text-xs text-gray-500">{ds.row_count?.toLocaleString()} rows · {ds.column_count} cols</span>
                        )}
                        {ds.file_type === 'pdf' && (
                          <span className="text-xs text-gray-500">
                            {ds.is_scanned ? 'Scanned — text search only' : 'Digital — tables + text search'}
                          </span>
                        )}
                      </div>
                    </div>
                    {analyticsSelected.has(ds.dataset_id) && (
                      <span className="shrink-0 text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-medium">Included</span>
                    )}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sample Data Format */}
      <div className="medical-card">
        <div className="medical-card-header">
          <h3 className="font-semibold text-gray-900">Expected Data Format</h3>
        </div>
        <div className="medical-card-content">
          <p className="text-gray-600 mb-4">Your file should include columns with medical data. Here's an example format:</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Age','Gender','Diagnosis','Medication','Primary Outcome','Adverse Events'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-gray-900">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-3 py-2 text-gray-600">65</td><td className="px-3 py-2 text-gray-600">Male</td><td className="px-3 py-2 text-gray-600">Type 2 Diabetes</td><td className="px-3 py-2 text-gray-600">Metformin 500mg</td><td className="px-3 py-2 text-gray-600">HbA1c 7.2%</td><td className="px-3 py-2 text-gray-600">None</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-3 py-2 text-gray-600">58</td><td className="px-3 py-2 text-gray-600">Female</td><td className="px-3 py-2 text-gray-600">Hypertension</td><td className="px-3 py-2 text-gray-600">Lisinopril 10mg</td><td className="px-3 py-2 text-gray-600">BP 130/85</td><td className="px-3 py-2 text-gray-600">Dry cough</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <div className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-yellow-800">
                <strong>Privacy Note:</strong> All patient data is automatically anonymized upon upload. Original patient identifiers are replaced with secure, anonymized IDs to maintain HIPAA compliance.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
